/**
 * =============================================================================
 * SPEQ55 RFP Pro - RFP Detail Page
 * =============================================================================
 *
 * Displays detailed information about a single RFP with inline editing.
 * All fields are editable by clicking on them, changes persist automatically.
 *
 * @module pages/rfps/RFPDetail
 * @author SPEQ55
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { get, del, put } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

/**
 * Status badge component with editable dropdown.
 */
function StatusBadge({ status, onChange, saving }) {
  const [editing, setEditing] = useState(false);
  const selectRef = useRef(null);

  const statusConfig = {
    draft: { label: 'Draft', className: 'badge bg-accent-100 text-accent-700' },
    tracking: {
      label: 'Tracking',
      className: 'badge bg-info-light text-info-dark',
    },
    no_bid: {
      label: 'No Bid',
      className: 'badge bg-accent-200 text-accent-600',
    },
    in_progress: {
      label: 'In Progress',
      className: 'badge bg-warning-light text-warning-dark',
    },
    submitted: {
      label: 'Submitted',
      className: 'badge bg-secondary-100 text-secondary-700',
    },
    won: {
      label: 'Won',
      className: 'badge bg-success-light text-success-dark',
    },
    lost: { label: 'Lost', className: 'badge bg-error-light text-error-dark' },
    cancelled: {
      label: 'Cancelled',
      className: 'badge bg-accent-200 text-accent-500',
    },
  };

  useEffect(() => {
    if (editing && selectRef.current) {
      selectRef.current.focus();
    }
  }, [editing]);

  const handleChange = (e) => {
    onChange(e.target.value);
    setEditing(false);
  };

  if (editing) {
    return (
      <select
        ref={selectRef}
        value={status}
        onChange={handleChange}
        onBlur={() => setEditing(false)}
        className="text-sm border border-primary-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        {Object.entries(statusConfig).map(([value, config]) => (
          <option key={value} value={value}>
            {config.label}
          </option>
        ))}
      </select>
    );
  }

  const config = statusConfig[status] || statusConfig.draft;
  return (
    <span
      onClick={() => setEditing(true)}
      className={`${config.className} text-sm cursor-pointer hover:ring-2 hover:ring-primary-300 transition-all ${saving ? 'opacity-50' : ''}`}
      title="Click to edit status"
    >
      {config.label}
    </span>
  );
}

/**
 * Editable text field component.
 */
function EditableField({
  label,
  value,
  fieldKey,
  onSave,
  type = 'text',
  placeholder = 'Click to edit',
  multiline = false,
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    if (editValue !== value) {
      setSaving(true);
      await onSave(fieldKey, editValue || null);
      setSaving(false);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setEditing(false);
    }
  };

  if (editing) {
    if (multiline) {
      return (
        <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="text-sm font-medium text-accent-500">{label}</dt>
          <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
            <textarea
              ref={inputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              rows={4}
              className="w-full border border-primary-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 text-accent-800"
              placeholder={placeholder}
            />
          </dd>
        </div>
      );
    }

    return (
      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-accent-500">{label}</dt>
        <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
          <input
            ref={inputRef}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="w-full border border-primary-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 text-accent-800"
            placeholder={placeholder}
          />
        </dd>
      </div>
    );
  }

  return (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-accent-500">{label}</dt>
      <dd
        onClick={() => setEditing(true)}
        className={`mt-1 text-sm sm:mt-0 sm:col-span-2 cursor-pointer hover:bg-primary-50 rounded px-1 -mx-1 transition-colors ${saving ? 'opacity-50' : ''} ${value ? 'text-accent-800' : 'text-accent-400 italic'}`}
        title="Click to edit"
      >
        {value || placeholder}
      </dd>
    </div>
  );
}

/**
 * Editable date field component.
 */
function EditableDateField({ label, value, fieldKey, onSave, bold = false }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Convert ISO date to YYYY-MM-DD for input
  const formatForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Format date for display
  const formatForDisplay = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  useEffect(() => {
    setEditValue(formatForInput(value));
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleSave = async () => {
    const newValue = editValue || null;
    const oldValue = formatForInput(value);
    if (newValue !== oldValue) {
      setSaving(true);
      await onSave(fieldKey, newValue);
      setSaving(false);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(formatForInput(value));
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div>
        <dt className="text-xs font-medium text-accent-500 uppercase">{label}</dt>
        <dd className="mt-1">
          <input
            ref={inputRef}
            type="date"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="w-full border border-primary-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-accent-800"
          />
        </dd>
      </div>
    );
  }

  const displayValue = formatForDisplay(value);

  return (
    <div>
      <dt className="text-xs font-medium text-accent-500 uppercase">{label}</dt>
      <dd
        onClick={() => setEditing(true)}
        className={`mt-1 text-sm cursor-pointer hover:bg-primary-50 rounded px-1 -mx-1 transition-colors ${saving ? 'opacity-50' : ''} ${bold ? 'font-semibold' : ''} ${displayValue ? 'text-accent-800' : 'text-accent-400 italic'}`}
        title="Click to edit"
      >
        {displayValue || 'Click to set date'}
      </dd>
    </div>
  );
}

/**
 * Editable select field component.
 */
function EditableSelectField({
  label,
  value,
  fieldKey,
  onSave,
  options,
  placeholder = 'Click to select',
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    if (editing && selectRef.current) {
      selectRef.current.focus();
    }
  }, [editing]);

  const handleChange = async (e) => {
    const newValue = e.target.value || null;
    if (newValue !== value) {
      setSaving(true);
      await onSave(fieldKey, newValue);
      setSaving(false);
    }
    setEditing(false);
  };

  const getDisplayValue = () => {
    if (!value) return null;
    const option = options.find((o) => o.value === value);
    return option ? option.label : value;
  };

  if (editing) {
    return (
      <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-accent-500">{label}</dt>
        <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
          <select
            ref={selectRef}
            value={value || ''}
            onChange={handleChange}
            onBlur={() => setEditing(false)}
            className="w-full border border-primary-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 text-accent-800"
          >
            <option value="">-- Select --</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </dd>
      </div>
    );
  }

  const displayValue = getDisplayValue();

  return (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-accent-500">{label}</dt>
      <dd
        onClick={() => setEditing(true)}
        className={`mt-1 text-sm sm:mt-0 sm:col-span-2 cursor-pointer hover:bg-primary-50 rounded px-1 -mx-1 transition-colors capitalize ${saving ? 'opacity-50' : ''} ${displayValue ? 'text-accent-800' : 'text-accent-400 italic'}`}
        title="Click to edit"
      >
        {displayValue || placeholder}
      </dd>
    </div>
  );
}

/**
 * Editable currency field component.
 */
function EditableCurrencyField({ label, value, fieldKey, onSave, currency = 'USD' }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    const numValue = editValue ? parseFloat(editValue) : null;
    if (numValue !== value) {
      setSaving(true);
      await onSave(fieldKey, numValue);
      setSaving(false);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div>
        <dt className="text-xs font-medium text-accent-500 uppercase">{label}</dt>
        <dd className="mt-1">
          <input
            ref={inputRef}
            type="number"
            step="0.01"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="w-full border border-primary-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-accent-800"
            placeholder="Enter amount"
          />
        </dd>
      </div>
    );
  }

  const displayValue = formatCurrency(value);

  return (
    <div>
      <dt className="text-xs font-medium text-accent-500 uppercase">{label}</dt>
      <dd
        onClick={() => setEditing(true)}
        className={`mt-1 text-sm cursor-pointer hover:bg-primary-50 rounded px-1 -mx-1 transition-colors ${saving ? 'opacity-50' : ''} ${displayValue ? 'text-accent-800' : 'text-accent-400 italic'}`}
        title="Click to edit"
      >
        {displayValue || 'Click to set amount'}
      </dd>
    </div>
  );
}

/**
 * Editable title component for the header.
 */
function EditableTitle({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    if (editValue && editValue !== value) {
      setSaving(true);
      await onSave('title', editValue);
      setSaving(false);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="text-2xl font-bold text-accent-800 border border-primary-300 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
    );
  }

  return (
    <h1
      onClick={() => setEditing(true)}
      className={`text-2xl font-bold text-accent-800 cursor-pointer hover:bg-primary-50 rounded px-1 -mx-1 transition-colors ${saving ? 'opacity-50' : ''}`}
      title="Click to edit title"
    >
      {value}
    </h1>
  );
}

/**
 * Editable text area section for description/notes.
 */
function EditableTextSection({
  title,
  value,
  fieldKey,
  onSave,
  placeholder = 'Click to add content...',
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [editing]);

  const handleSave = async () => {
    if (editValue !== value) {
      setSaving(true);
      await onSave(fieldKey, editValue || null);
      setSaving(false);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-accent-800">{title}</h2>
        </div>
        <div className="card-body">
          <textarea
            ref={textareaRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            rows={6}
            className="w-full border border-primary-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 text-accent-700"
            placeholder={placeholder}
          />
          <p className="mt-2 text-xs text-accent-400">
            Click outside to save, or press Tab to move to next field
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-lg font-semibold text-accent-800">{title}</h2>
      </div>
      <div
        onClick={() => setEditing(true)}
        className={`card-body cursor-pointer hover:bg-primary-50 transition-colors ${saving ? 'opacity-50' : ''}`}
        title="Click to edit"
      >
        {value ? (
          <p className="text-accent-700 whitespace-pre-wrap">{value}</p>
        ) : (
          <p className="text-accent-400 italic">{placeholder}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Format date for display (read-only fields like Created/Updated).
 */
function formatDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// =============================================================================
// RFP DETAIL COMPONENT
// =============================================================================

function RFPDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasMinRole } = useAuth();

  // State
  const [rfp, setRfp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [viewingFile, setViewingFile] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Permissions
  const canEdit = hasMinRole('org_admin');
  const canDelete = hasMinRole('admin_sales');

  // Category options (must match backend validation)
  const categoryOptions = [
    { value: 'environmental', label: 'Environmental' },
    { value: 'civil', label: 'Civil' },
    { value: 'mechanical', label: 'Mechanical' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'structural', label: 'Structural' },
    { value: 'geotechnical', label: 'Geotechnical' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'water_resources', label: 'Water Resources' },
    { value: 'other', label: 'Other' },
  ];

  // Source options
  const sourceOptions = [
    { value: 'direct', label: 'Direct' },
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'sam_gov', label: 'SAM.gov' },
    { value: 'grant', label: 'Grant' },
    { value: 'other', label: 'Other' },
  ];

  /**
   * Fetch RFP details.
   */
  useEffect(() => {
    const fetchRFP = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await get(`/rfps/${id}`);
        setRfp(response.data.rfp);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('RFP not found');
        } else if (err.response?.status === 403) {
          setError('You do not have permission to view this RFP');
        } else {
          setError('Failed to load RFP details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRFP();
  }, [id]);

  /**
   * Handle field save.
   */
  const handleSave = async (fieldKey, value) => {
    try {
      const response = await put(`/rfps/${id}`, { [fieldKey]: value });
      setRfp(response.data.rfp);
      setSaveMessage('Saved');
      setTimeout(() => setSaveMessage(null), 2000);
    } catch {
      setError('Failed to save changes. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  /**
   * Handle RFP deletion.
   */
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await del(`/rfps/${id}`);
      navigate('/app/rfps', { replace: true });
    } catch {
      setError('Failed to delete RFP');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Handle viewing the uploaded PDF file.
   */
  const handleViewFile = async () => {
    setViewingFile(true);
    try {
      const response = await api.get(`/rfps/${id}/download`, {
        responseType: 'blob',
      });

      // Create blob URL and open in new tab
      const blob = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;

      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      setError('Failed to open file. Please try again.');
    } finally {
      setViewingFile(false);
    }
  };

  /**
   * Handle file upload button click.
   */
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Handle file selection and upload.
   */
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF or Word document (.pdf, .doc, .docx)');
      setTimeout(() => setError(null), 5000);
      return;
    }

    // Validate file size (20MB max)
    if (file.size > 20 * 1024 * 1024) {
      setError('File is too large. Maximum size is 20MB.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/rfps/${id}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (response.data.success) {
        // Update RFP with new file information
        setRfp(response.data.rfp);
        setSaveMessage('File uploaded successfully');
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-accent-500">Loading RFP...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !rfp) {
    return (
      <div className="space-y-6">
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
        <Link to="/app/rfps" className="btn-outline">
          Back to RFPs
        </Link>
      </div>
    );
  }

  if (!rfp) return null;

  return (
    <div className="space-y-6">
      {/* Save/Error notification */}
      {(saveMessage || error) && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2 rounded shadow-lg ${saveMessage ? 'bg-success-light text-success-dark' : 'bg-error-light text-error-dark'}`}
        >
          {saveMessage || error}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/app/rfps" className="text-accent-500 hover:text-accent-700">
              <span>RFPs</span>
            </Link>
            <span className="text-accent-400">/</span>
            <span className="text-accent-800 font-mono">{rfp.rfpNumber}</span>
          </div>
          <EditableTitle value={rfp.title} onSave={handleSave} />
          <div className="mt-2 flex items-center gap-3">
            <StatusBadge status={rfp.status} onChange={(value) => handleSave('status', value)} />
            {rfp.category && (
              <span className="text-sm text-accent-500 capitalize">
                {rfp.category.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          {rfp.filePath && (
            <>
              <Link to={`/app/rfps/${id}/analyze`} className="btn-primary flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                RFP Analysis
              </Link>
              <button onClick={handleViewFile} disabled={viewingFile} className="btn-outline">
                {viewingFile ? 'Opening...' : 'View PDF'}
              </button>
            </>
          )}
          {!rfp.filePath && canEdit && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
              />
              <button
                onClick={handleUploadClick}
                disabled={uploading}
                className="btn-primary flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                {uploading ? `Uploading ${uploadProgress}%` : 'Upload PDF'}
              </button>
            </>
          )}
          {canEdit && (
            <Link to={`/app/rfps/${id}/edit`} className="btn-outline">
              Edit Form
            </Link>
          )}
          {canDelete && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn-outline text-error hover:bg-error-light"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-accent-800">Delete RFP?</h3>
            <p className="mt-2 text-accent-600">
              Are you sure you want to delete &quot;{rfp.title}&quot;? This action cannot be undone
              and will also delete any associated proposals.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn-primary bg-error hover:bg-error-dark"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Details Card */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-accent-800">RFP Details</h2>
              <p className="text-xs text-accent-400 mt-1">Click any field to edit</p>
            </div>
            <div className="card-body">
              <dl className="divide-y divide-accent-200">
                <EditableField
                  label="RFP Number"
                  value={rfp.rfpNumber}
                  fieldKey="rfpNumber"
                  onSave={handleSave}
                  placeholder="Enter RFP number"
                />
                <EditableField
                  label="Company"
                  value={rfp.companyName}
                  fieldKey="companyName"
                  onSave={handleSave}
                  placeholder="Enter company name"
                />
                <EditableSelectField
                  label="Category"
                  value={rfp.category}
                  fieldKey="category"
                  onSave={handleSave}
                  options={categoryOptions}
                  placeholder="Select category"
                />
                <EditableField
                  label="Subcategory"
                  value={rfp.subcategory}
                  fieldKey="subcategory"
                  onSave={handleSave}
                  placeholder="Enter subcategory"
                />
                <EditableSelectField
                  label="Source"
                  value={rfp.source}
                  fieldKey="source"
                  onSave={handleSave}
                  options={sourceOptions}
                  placeholder="Select source"
                />
                <EditableField
                  label="Source URL"
                  value={rfp.sourceUrl}
                  fieldKey="sourceUrl"
                  onSave={handleSave}
                  type="url"
                  placeholder="Enter source URL"
                />
                <EditableField
                  label="Lead"
                  value={rfp.leadUserName}
                  fieldKey="leadUserName"
                  onSave={handleSave}
                  placeholder="Enter lead name"
                />
              </dl>
            </div>
          </div>

          {/* Description */}
          <EditableTextSection
            title="Description"
            value={rfp.description}
            fieldKey="description"
            onSave={handleSave}
            placeholder="Click to add a description..."
          />

          {/* Decision Notes */}
          <EditableTextSection
            title="Decision Notes"
            value={rfp.decisionNotes}
            fieldKey="decisionNotes"
            onSave={handleSave}
            placeholder="Click to add decision notes..."
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Timeline */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-accent-800">Timeline</h2>
              <p className="text-xs text-accent-400 mt-1">Click any date to edit</p>
            </div>
            <div className="card-body">
              <dl className="space-y-4">
                <EditableDateField
                  label="Issue Date"
                  value={rfp.issueDate}
                  fieldKey="issueDate"
                  onSave={handleSave}
                />
                <EditableDateField
                  label="Questions Due"
                  value={rfp.questionsDueDate}
                  fieldKey="questionsDueDate"
                  onSave={handleSave}
                />
                <EditableDateField
                  label="Submission Deadline"
                  value={rfp.submissionDeadline}
                  fieldKey="submissionDeadline"
                  onSave={handleSave}
                  bold
                />
                <EditableDateField
                  label="Expected Award Date"
                  value={rfp.expectedAwardDate}
                  fieldKey="expectedAwardDate"
                  onSave={handleSave}
                />
                <EditableDateField
                  label="Project Start"
                  value={rfp.projectStartDate}
                  fieldKey="projectStartDate"
                  onSave={handleSave}
                />
              </dl>
            </div>
          </div>

          {/* Budget */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-accent-800">Budget</h2>
              <p className="text-xs text-accent-400 mt-1">Click to edit</p>
            </div>
            <div className="card-body">
              <dl className="space-y-4">
                <EditableCurrencyField
                  label="Minimum"
                  value={rfp.estimatedBudgetMin}
                  fieldKey="estimatedBudgetMin"
                  onSave={handleSave}
                  currency={rfp.budgetCurrency || 'USD'}
                />
                <EditableCurrencyField
                  label="Maximum"
                  value={rfp.estimatedBudgetMax}
                  fieldKey="estimatedBudgetMax"
                  onSave={handleSave}
                  currency={rfp.budgetCurrency || 'USD'}
                />
              </dl>
            </div>
          </div>

          {/* Metadata (read-only) */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-accent-800">Metadata</h2>
            </div>
            <div className="card-body">
              <dl className="space-y-4 text-sm">
                <div>
                  <dt className="text-xs font-medium text-accent-500 uppercase">Created</dt>
                  <dd className="mt-1 text-accent-800">{formatDate(rfp.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-accent-500 uppercase">Last Updated</dt>
                  <dd className="mt-1 text-accent-800">{formatDate(rfp.updatedAt)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RFPDetail;
